import { CheckInDetail, Service } from '../interfaces/check_in';
import { CheckOutDetail, CheckOutItemDetail } from '../interfaces/check_out';
import { HealthHistoryDetail, RegistrationHealthHistoryDetail } from '../interfaces/health_history';
import { OwnerDetail, OwnerType } from '../interfaces/owner';
import { PetCheckIn, PetCheckInsDetail } from '../interfaces/report';
import { PetDetail, PetType, RegistrationPetDetail } from '../interfaces/pet';
import { convertBooleanToYesNo, convertYesNoToBoolean, convertYesNoToBooleanString } from '../utils/form-util';
import { convertUTCToLocal, getDateFormatted, getTimeFromDateTimeString } from '../utils/date-util';

import { BranchDetail } from '../interfaces/branch';
import { FormGroup } from '@angular/forms';
import { Injectable } from '@angular/core';
import { InvoiceDetail, InvoiceItem } from '../interfaces/invoice';
import { PickUpDetail } from '../interfaces/pick-up';
import { UserDetail, UserPermission } from '../interfaces/user';
import { QRCodeDetail } from '../interfaces/qr-code';

@Injectable({
  providedIn: 'root'
})
export class DataMappingService {
  mapDataToForm(data: any): any {
    // Your mapping logic for form data
    return {
      firstName: data.first_name,
      lastName: data.last_name,
      age: data.age,
      emailAddress: data.email_address
    };
  }

  mapOwnerTypeDataToInterface(data: any): OwnerType {
    return {
      id: data.id,
      name: data.name,
      note: data.note,
    };
  }

  mapOwnerDetailDataToInterface(data: any): OwnerDetail {
    return {
      id: data.id,
      firstName: data.first_name,
      lastName: data.last_name,
      phone: data.phone,
      email: data.email,
      address: data.address,
      nationality: data.nationality,
      emergencyContact: data.emergency_contact,
      vetDetails: data.vet_details,
      howDidYouHearAboutGoodDog: data.how_did_you_hear_about_good_dog,
      howDidYouHearAboutGoodDogOther: data.how_did_you_hear_about_good_dog_other,
      type: this.mapOwnerTypeDataToInterface(data.type)
    };
  }

  mapRegistrationOwnerDetailFormToJson(formGroup: FormGroup): {} {
    const formValues = formGroup.value
    return {
      first_name: formValues.ownerFirstName,
      last_name: formValues.ownerLastName,
      phone: formValues.ownerPhone,
      email: formValues.ownerEmail,
      address: formValues.ownerAddress,
      nationality: formValues.ownerNationality,
      emergency_contact: formValues.ownerEmergencyContact,
      vet_details: formValues.ownerVetDetails,
      how_did_you_hear_about_good_dog: formValues.howDidYouHearAboutGoodDog,
      how_did_you_hear_about_good_dog_other: formValues.howDidYouHearAboutGoodDogOther,
    };
  }

  mapOwnersDetailDataToInterfaces(data: any[]): OwnerDetail [] {
    return data.map(item => this.mapOwnerDetailDataToInterface(item));
  }

  mapInvoiceItemDataToInterface(data: any): InvoiceItem {
    return {
      id: data.id,
      petId: data.pet_id,
      petName: data.pet_name,
      petCode: data.pet_code,
      itemType: data.item_type,
      itemId: data.item_id,
      itemName: data.item_name,
      quantity: data.quantity,
      unitPrice: data.unit_price,
      discount: data.discount,
      note: data.note,
    }
  }

  mapInvoiceItemsDataToInterfaces(data: any[]): InvoiceItem [] {
    return data.map(item => this.mapInvoiceItemDataToInterface(item));
  }

  mapInvoiceDetailDataToInterface(data: any): InvoiceDetail {
    return {
      id: data.id,
      date: data.date,
      code: data.code,
      checkInId: data.check_in_id,
      checkInDate: data.check_in_date,
      checkOutId: data.check_out_id,
      checkOutDate: data.check_out_date,
      petId: data.pet_id,
      petName: data.pet_name,
      petCode: data.pet_code,
      petWeight: data.pet_weight,
      ownerId: data.owner_id,
      ownerName: data.owner_name,
      ownerPhone: data.owner_phone,
      ownerEmail: data.owner_email,
      ownerAddress: data.owner_address,
      ownerTypeId: data.owner_type_id,
      ownerTypeName: data.owner_type_name,
      serviceCharges: data.invoice_to_service,
      items: this.mapInvoiceItemsDataToInterfaces(data.items)
    };
  }

  mapHealthHistoryDataToInterface(data: any): HealthHistoryDetail {
    return {
      id: data.id,
      isFullyVaccinated: data.is_fully_vaccinated,
      vaccinationProof: data.vaccination_proof,
      isRabiesVaccinated: data.is_rabies_vaccinated,
      isSkinProblems: data.is_skin_problems,
      isTicksFleesTreated: data.is_ticks_flees_treated,
      ticksFleesTreatedDate: data.ticks_flees_treated_date,
      ticksFleesTreatedHowTo: data.ticks_flees_treated_how_to,
      isRecentlyDeWormed: data.is_recently_de_wormed,
      recentlyDeWormedDate: data.recently_de_wormed_date,
      isSpecialDietaryRequirements: data.is_special_dietary_requirements,
      specialDietaryRequirementsDescription: data.special_dietary_requirements_description,
      isIllLast30Days: data.is_ill_last_30_days,
      illLast30DaysExplanation: data.ill_last_30_days_explanation,
      isDisplayingSymptomsCoughingSneezingUpsetStomach: data.is_displaying_symptoms_coughing_sneezing_upset_stomach,
      isMedicalConditionsInjuriesIllnesses: data.is_medical_conditions_injuries_illnesses,
      medicalConditionsInjuriesIllnessesDetails: data.medical_conditions_injuries_illnesses_details,
      isAllergiesFoodSensitivities: data.is_allergies_food_sensitivities,
      allergiesFoodSensitivitiesDetails: data.allergies_food_sensitivities_details
    }
  }

  mapRegistrationPetHealthHistoryFormToInterface(formGroup: FormGroup): RegistrationHealthHistoryDetail {
    const formValues = formGroup.value
    return {
      isFullyVaccinated: convertYesNoToBoolean(formValues.isFullyVaccinated),
      vaccinationProof: formValues.vaccinatedProof,
      isRabiesVaccinated: convertYesNoToBoolean(formValues.isRabiesVaccinated),
      isSkinProblems: convertYesNoToBoolean(formValues.isSkinProblems),
      isTicksFleesTreated: convertYesNoToBoolean(formValues.isTicksFleesTreated),
      ticksFleesTreatedDate: formValues.ticksFleesTreatedDate,
      ticksFleesTreatedHowTo: formValues.ticksFleesTreatedHowTo,
      isRecentlyDeWormed: convertYesNoToBoolean(formValues.isRecentlyDeWormed),
      recentlyDeWormedDate: formValues.recentlyDeWormedDate,
      isSpecialDietaryRequirements: convertYesNoToBoolean(formValues.isSpecialDietaryRequirements),
      specialDietaryRequirementsDescription: formValues.specialDietaryRequirementsDescription,
      isIllLast30Days: convertYesNoToBoolean(formValues.isIllLast30Days),
      illLast30DaysExplanation: formValues.illLast30DaysExplanation,
      isDisplayingSymptomsCoughingSneezingUpsetStomach: convertYesNoToBoolean(formValues.isDisplayingSymptomsCoughingSneezingUpsetStomach),
      isMedicalConditionsInjuriesIllnesses: convertYesNoToBoolean(formValues.isMedicalConditionsInjuriesIllnesses),
      medicalConditionsInjuriesIllnessesDetails: formValues.medicalConditionsInjuriesIllnessesDetails,
      isAllergiesFoodSensitivities: convertYesNoToBoolean(formValues.isAllergiesFoodSensitivities),
      allergiesFoodSensitivitiesDetails: formValues.allergiesFoodSensitivitiesDetails
    };
  }

  mapRegistrationPetHealthHistoryInterfaceToJson(health_history: RegistrationHealthHistoryDetail): {} {
    return {
      is_fully_vaccinated: health_history.isFullyVaccinated,
      vaccination_proof: health_history.vaccinationProof,
      is_rabies_vaccinated: health_history.isRabiesVaccinated,
      is_skin_problems: health_history.isSkinProblems,
      is_ticks_flees_treated: health_history.isTicksFleesTreated,
      ticks_flees_treated_date: health_history.ticksFleesTreatedDate,
      ticks_flees_treated_how_to: health_history.ticksFleesTreatedHowTo,
      is_recently_de_wormed: health_history.isRecentlyDeWormed,
      recently_de_wormed_date: health_history.recentlyDeWormedDate,
      is_special_dietary_requirements: health_history.isSpecialDietaryRequirements,
      special_dietary_requirements_description: health_history.specialDietaryRequirementsDescription,
      is_ill_last_30_days: health_history.isIllLast30Days,
      ill_last_30_days_explanation: health_history.illLast30DaysExplanation,
      is_displaying_symptoms_coughing_sneezing_upset_stomach: health_history.isDisplayingSymptomsCoughingSneezingUpsetStomach,
      is_medical_conditions_injuries_illnesses: health_history.isMedicalConditionsInjuriesIllnesses,
      medical_conditions_injuries_illnesses_details: health_history.medicalConditionsInjuriesIllnessesDetails,
      is_allergies_food_sensitivities: health_history.isAllergiesFoodSensitivities,
      allergies_food_sensitivities_details: health_history.allergiesFoodSensitivitiesDetails
    }
  }

  mapRegistrationPickUpFormToJson(formGroup: FormGroup): {} {
    const formValues = formGroup.value
    const date_time = `${formValues.pickUpDate} ${formValues.pickUpTime}:00`
    return {
      first_name: formValues.pickUpFirstName,
      last_name: formValues.pickUpLastName,
      phone: formValues.pickUpPhone,
      address: formValues.pickUpAddress,
      date_time: date_time
    };
  }

  mapPetDetailDataToInterface(data: any): PetDetail {
    return {
      id: data.id,
      name: data.name,
      sex: data.sex,
      code: data.code,
      weight: data.weight,
      lastHeatDate: data.last_heat_date,
      age: data.age,
      breed: data.breed,
      isSpayedNeutered: data.is_spayed_neutered,
      isFriendly: data.is_friendly,
      isSensitiveBodyOrPhysicalRestriction: data.is_sensitive_body_or_physical_restriction,
      sensitiveBodyOrPhysicalRestrictionDetails: data.sensitive_body_or_physical_restriction_details,
      isAllowedPhotoVideoUsage: data.is_allowed_photo_video_usage,
      owner: data.owner!=null? this.mapOwnerDetailDataToInterface(data.owner) : data.owner,
      diseases: data.diseases,
      vaccines: data.vaccines,
      healthHistory: data.health_history==null? data.health_history : this.mapHealthHistoryDataToInterface(data.health_history),
      type: data.type!=null? this.mapPetTypeDataToInterface(data.type) : data.type,
      image: data.image,
      imageFileName: data.imageFileName,
    }
  }

  mapPetsDetailDataToInterface(data: any[]): PetDetail[] {
    return data.map(item => this.mapPetDetailDataToInterface(item));
  }

  mapRegistrationPetDetailFormToInterface(formGroup: FormGroup): RegistrationPetDetail {
    const formValues = formGroup.value
    return {
      type: formValues.petType.id,
      name: formValues.name,
      sex: formValues.sex,
      code: formValues.code,
      weight: formValues.weight,
      lastHeatDate: formValues.lastHeatDate,
      age: formValues.age,
      breed: formValues.breed,
      isSpayedNeutered: convertYesNoToBoolean(formValues.isSpayedNeutered),
      isFriendly: convertYesNoToBoolean(formValues.isFriendly),
      isSensitiveBodyOrPhysicalRestriction: convertYesNoToBoolean(formValues.isSensitiveBodyOrPhysicalRestriction),
      sensitiveBodyOrPhysicalRestrictionDetails: formValues.sensitiveBodyOrPhysicalRestrictionDetails,
      isAllowedPhotoVideoUsage: convertYesNoToBoolean(formValues.isAllowedPhotoVideoUsage),
      healthHistory: this.mapRegistrationPetHealthHistoryFormToInterface(formGroup)
    }
  }

  mapRegistrationPetDetailInterfaceToJson(pet: RegistrationPetDetail): {} {
    return {
      type_id: pet.type,
      name: pet.name,
      sex: pet.sex,
      code: pet.code,
      weight: pet.weight,
      last_heat_date: pet.lastHeatDate,
      age: pet.age,
      breed: pet.breed,
      is_spayed_neutered: pet.isSpayedNeutered,
      is_friendly: pet.isFriendly,
      is_sensitive_body_or_physical_restriction: pet.isSensitiveBodyOrPhysicalRestriction,
      sensitive_body_or_physical_restriction_details: pet.sensitiveBodyOrPhysicalRestrictionDetails,
      is_allowed_photo_video_usage: pet.isAllowedPhotoVideoUsage,
      health_history: this.mapRegistrationPetHealthHistoryInterfaceToJson(pet.healthHistory)
    }
  }

  mapPetUpdateHealthHistoryFormToInterface(formGroup: FormGroup): RegistrationHealthHistoryDetail {
    const formValues = formGroup.value
    return {
      isFullyVaccinated: convertYesNoToBoolean(formValues.isFullyVaccinated),
      vaccinationProof: formValues.vaccinatedProof,
      isRabiesVaccinated: convertYesNoToBoolean(formValues.isRabiesVaccinated),
      isSkinProblems: convertYesNoToBoolean(formValues.isSkinProblems),
      isTicksFleesTreated: convertYesNoToBoolean(formValues.isTicksFleesTreated),
      ticksFleesTreatedDate: formValues.ticksFleesTreatedDate,
      ticksFleesTreatedHowTo: formValues.ticksFleesTreatedHowTo,
      isRecentlyDeWormed: convertYesNoToBoolean(formValues.isRecentlyDeWormed),
      recentlyDeWormedDate: formValues.recentlyDeWormedDate,
      isSpecialDietaryRequirements: convertYesNoToBoolean(formValues.isSpecialDietaryRequirements),
      specialDietaryRequirementsDescription: formValues.specialDietaryRequirementsDescription,
      isIllLast30Days: convertYesNoToBoolean(formValues.isIllLast30Days),
      illLast30DaysExplanation: formValues.illLast30DaysExplanation,
      isDisplayingSymptomsCoughingSneezingUpsetStomach: convertYesNoToBoolean(formValues.isDisplayingSymptomsCoughingSneezingUpsetStomach),
      isMedicalConditionsInjuriesIllnesses: convertYesNoToBoolean(formValues.isMedicalConditionsInjuriesIllnesses),
      medicalConditionsInjuriesIllnessesDetails: formValues.medicalConditionsInjuriesIllnessesDetails,
      isAllergiesFoodSensitivities: convertYesNoToBoolean(formValues.isAllergiesFoodSensitivities),
      allergiesFoodSensitivitiesDetails: formValues.allergiesFoodSensitivitiesDetails
    };
  }

  mapPetUpdateDetailFormToJson(formGroup: FormGroup): {} {
    const formValues = formGroup.value
    return {
      type_id: formValues.petType.id,
      name: formValues.name,
      sex: formValues.sex,
      code: formValues.code,
      weight: formValues.weight,
      lastHeatDate: formValues.lastHeatDate,
      age: formValues.age,
      breed: formValues.breed,
      is_spayed_neutered: convertYesNoToBoolean(formValues.isSpayedNeutered),
      is_friendly: convertYesNoToBoolean(formValues.isFriendly),
      is_sensitive_body_or_physical_restriction: convertYesNoToBoolean(formValues.isSensitiveBodyOrPhysicalRestriction),
      sensitive_body_or_physical_restriction_details: formValues.sensitiveBodyOrPhysicalRestrictionDetails,
      is_allowed_photo_video_usage: convertYesNoToBoolean(formValues.isAllowedPhotoVideoUsage),
      health_history: this.mapPetUpdateHealthHistoryFormToJson(formGroup)
    }
  }

  mapPetUpdateDetailFormGroupToFormData(formGroup: FormGroup): FormData {
    const formData = new FormData();
    const formValues = formGroup.value;
    formData.append('name', formValues.name);
    formData.append('sex', formValues.sex);
    formData.append('code', formValues.code);
    formData.append('weight', formValues.weight);
    formData.append('last_heat_date', formValues.lastHeatDate === null? '' : formValues.lastHeatDate);
    formData.append('age', formValues.age);
    formData.append('breed', formValues.breed);
    formData.append('is_spayed_neutered', convertYesNoToBooleanString(formValues.isSpayedNeutered));
    formData.append('is_friendly', convertYesNoToBooleanString(formValues.isFriendly));
    formData.append('is_sensitive_body_or_physical_restriction', convertYesNoToBooleanString(formValues.isSensitiveBodyOrPhysicalRestriction));
    formData.append('sensitive_body_or_physical_restriction_details', formValues.sensitiveBodyOrPhysicalRestrictionDetails === null? '' : formValues.sensitiveBodyOrPhysicalRestrictionDetails);
    formData.append('is_allowed_photo_video_usage', convertYesNoToBooleanString(formValues.isAllowedPhotoVideoUsage));
    formData.append('is_fully_vaccinated', convertYesNoToBooleanString(formValues.isFullyVaccinated));
    formData.append('vaccination_proof', formValues.vaccinatedProof);
    formData.append('is_rabies_vaccinated', convertYesNoToBooleanString(formValues.isRabiesVaccinated));
    formData.append('is_skin_problems', convertYesNoToBooleanString(formValues.isSkinProblems));
    formData.append('is_ticks_flees_treated', convertYesNoToBooleanString(formValues.isTicksFleesTreated));
    formData.append('ticks_flees_treated_date', formValues.ticksFleesTreatedDate === null? '' : formValues.ticksFleesTreatedDate);
    formData.append('ticks_flees_treated_how_to', formValues.ticksFleesTreatedHowTo === null? '' : formValues.ticksFleesTreatedHowTo);
    formData.append('is_recently_de_wormed', convertYesNoToBooleanString(formValues.isRecentlyDeWormed));
    formData.append('recently_de_wormed_date', formValues.recentlyDeWormedDate === null? '' : formValues.recentlyDeWormedDate);
    formData.append('is_special_dietary_requirements', convertYesNoToBooleanString(formValues.isSpecialDietaryRequirements));
    formData.append('special_dietary_requirements_description', formValues.specialDietaryRequirementsDescription === null? '' : formValues.specialDietaryRequirementsDescription);
    formData.append('is_ill_last_30_days', convertYesNoToBooleanString(formValues.isIllLast30Days));
    formData.append('ill_last_30_days_explanation', formValues.illLast30DaysExplanation === null? '' : formValues.illLast30DaysExplanation);
    formData.append('is_displaying_symptoms_coughing_sneezing_upset_stomach', convertYesNoToBooleanString(formValues.isDisplayingSymptomsCoughingSneezingUpsetStomach));
    formData.append('is_medical_conditions_injuries_illnesses', convertYesNoToBooleanString(formValues.isMedicalConditionsInjuriesIllnesses));
    formData.append('medical_conditions_injuries_illnesses_details', formValues.medicalConditionsInjuriesIllnessesDetails === null? '' : formValues.medicalConditionsInjuriesIllnessesDetails);
    formData.append('is_allergies_food_sensitivities', convertYesNoToBooleanString(formValues.isAllergiesFoodSensitivities));
    formData.append('allergies_food_sensitivities_details', formValues.allergiesFoodSensitivitiesDetails === null? '' : formValues.allergiesFoodSensitivitiesDetails);
    // formData.append('health_history', this.mapPetUpdateHealthHistoryFormToJson(formGroup));
    // formData.append('', formValues.);
    return formData;
  }

  mapPetUpdateHealthHistoryFormToJson(formGroup: FormGroup): {} {
    const formValues = formGroup.value
    return {
      is_fully_vaccinated: convertYesNoToBoolean(formValues.isFullyVaccinated),
      vaccination_proof: formValues.vaccinatedProof,
      is_rabies_vaccinated: convertYesNoToBoolean(formValues.isRabiesVaccinated),
      is_skin_problems: convertYesNoToBoolean(formValues.isSkinProblems),
      is_ticks_flees_treated: convertYesNoToBoolean(formValues.isTicksFleesTreated),
      ticks_flees_treated_date: formValues.ticksFleesTreatedDate,
      ticks_flees_treated_how_to: formValues.ticksFleesTreatedHowTo,
      is_recently_de_wormed: convertYesNoToBoolean(formValues.isRecentlyDeWormed),
      recently_de_wormed_date: formValues.recentlyDeWormedDate,
      is_special_dietary_requirements: convertYesNoToBoolean(formValues.isSpecialDietaryRequirements),
      special_dietary_requirements_description: formValues.specialDietaryRequirementsDescription,
      is_ill_last_30_days: convertYesNoToBoolean(formValues.isIllLast30Days),
      ill_last_30_days_explanation: formValues.illLast30DaysExplanation,
      is_displaying_symptoms_coughing_sneezing_upset_stomach: convertYesNoToBoolean(formValues.isDisplayingSymptomsCoughingSneezingUpsetStomach),
      is_medical_conditions_injuries_illnesses: convertYesNoToBoolean(formValues.isMedicalConditionsInjuriesIllnesses),
      medical_conditions_injuries_illnesses_details: formValues.medicalConditionsInjuriesIllnessesDetails,
      is_allergies_food_sensitivities: convertYesNoToBoolean(formValues.isAllergiesFoodSensitivities),
      allergies_food_sensitivities_details: formValues.allergiesFoodSensitivitiesDetails
    };
  }

  mapPetUpdateHealthHistoryInterfaceToJson(health_history: RegistrationHealthHistoryDetail): {} {
    return {
      is_fully_vaccinated: health_history.isFullyVaccinated,
      vaccination_proof: health_history.vaccinationProof,
      is_rabies_vaccinated: health_history.isRabiesVaccinated,
      is_skin_problems: health_history.isSkinProblems,
      is_ticks_flees_treated: health_history.isTicksFleesTreated,
      ticks_flees_treated_date: health_history.ticksFleesTreatedDate,
      ticks_flees_treated_how_to: health_history.ticksFleesTreatedHowTo,
      is_recently_de_wormed: health_history.isRecentlyDeWormed,
      recently_de_wormed_date: health_history.recentlyDeWormedDate,
      is_special_dietary_requirements: health_history.isSpecialDietaryRequirements,
      special_dietary_requirements_description: health_history.specialDietaryRequirementsDescription,
      is_ill_last_30_days: health_history.isIllLast30Days,
      ill_last_30_days_explanation: health_history.illLast30DaysExplanation,
      is_displaying_symptoms_coughing_sneezing_upset_stomach: health_history.isDisplayingSymptomsCoughingSneezingUpsetStomach,
      is_medical_conditions_injuries_illnesses: health_history.isMedicalConditionsInjuriesIllnesses,
      medical_conditions_injuries_illnesses_details: health_history.medicalConditionsInjuriesIllnessesDetails,
      is_allergies_food_sensitivities: health_history.isAllergiesFoodSensitivities,
      allergies_food_sensitivities_details: health_history.allergiesFoodSensitivitiesDetails
    }
  }

  mapPetUpdateDetailInterfaceToJson(pet: RegistrationPetDetail): {} {
    return {
      type_id: pet.type,
      name: pet.name,
      sex: pet.sex,
      code: pet.code,
      weight: pet.weight,
      last_heat_date: pet.lastHeatDate,
      age: pet.age,
      breed: pet.breed,
      is_spayed_neutered: pet.isSpayedNeutered,
      is_friendly: pet.isFriendly,
      is_sensitive_body_or_physical_restriction: pet.isSensitiveBodyOrPhysicalRestriction,
      sensitive_body_or_physical_restriction_details: pet.sensitiveBodyOrPhysicalRestrictionDetails,
      is_allowed_photo_video_usage: pet.isAllowedPhotoVideoUsage,
      health_history: this.mapRegistrationPetHealthHistoryInterfaceToJson(pet.healthHistory)
    }
  }

  mapBranchDetailDataToInterface(data: any): BranchDetail {
    return {
      id: data.id,
      name: data.name,
      address: data.address,
      phone: data.phone
    }
  }

  mapCheckInDetailDataToInterface(data: any): CheckInDetail {
    return {
      id: data.id,
      date: data.date,
      branch: this.mapBranchDetailDataToInterface(data.branch),
      pets: this.mapPetsDetailDataToInterface(data.pets),
      services: data.services,
      isCheckedOut: data.is_checked_out,
      checkedOutDate: data.checked_out_date
    }
  }

  mapCheckInsDetailDataToInterface(data: any[]): CheckInDetail[] {
    return data.map(item => this.mapCheckInDetailDataToInterface(item));
  }

  mapCheckOutItemDetailDataToInterface(data: any): CheckOutItemDetail {
    return {
      id: data.id,
      pet: this.mapPetDetailDataToInterface(data.pet),
      itemType: data.item_type,
      itemId: data.item_id,
      itemName: data.item_name,
      unitPrice: data.unit_price,
      quantity: data.quantity,
      note: data.note
    }
  }

  mapCheckOutItemsDetailDataToInterfaces(data: any[]): CheckOutItemDetail[] {
    return data.map(item => this.mapCheckOutItemDetailDataToInterface(item));
  }

  mapCheckOutDetailDataToInterface(data: any): CheckOutDetail {
    return {
      id: data.id,
      date: data.date,
      discount: data.discount,
      checkIn: this.mapCheckInDetailDataToInterface(data.check_in),
      items: this.mapCheckOutItemsDetailDataToInterfaces(data.items),
    }
  }

  mapCheckOutsDetailDataToInterfaces(data: any[]): CheckOutDetail[] {
    return data.map(item => this.mapCheckOutDetailDataToInterface(item));
  }

  mapPetsDetailDataToInterfaces(data: any[]): PetDetail[] {
    return data.map(item => this.mapPetDetailDataToInterface(item));
  }

  mapPickUpDetailDataToInterface(data: any): PickUpDetail {
    const localTime = convertUTCToLocal(data.date_time)
    console.log(`data.date_time: ${data.date_time}`);
    console.log(`local time: ${localTime}`);
    console.log(`getTimeFromDateTimeString(localTime): ${getTimeFromDateTimeString(localTime)}`);
    return {
      id: data.id,
      firstName: data.first_name,
      lastName: data.last_name,
      phone: data.phone,
      address: data.address,
      date: getDateFormatted(localTime, 'yyyy-MM-dd'),
      time: getTimeFromDateTimeString(localTime),
    };
  }

  mapPickUpsDetailDataToInterfaces(data: any[]): PickUpDetail[] {
    return data.map(item => this.mapPickUpDetailDataToInterface(item));
  }

  mapUserPermissionDataToInterface(data: any): UserPermission {
    return {
      id: data.id,
      name: data.name,
      codename: data.codename,
    };
  }

  mapUserPermissionsDataToInterfaces(data: any[]): UserPermission[] {
    return data.map(item => this.mapUserPermissionDataToInterface(item));
  }

  mapUserDetailDataToInterface(data: any): UserDetail {
    return {
      id: data.id,
      username: data.username,
      firstName: data.first_name,
      lastName: data.last_name,
      email: data.email,
      branch: this.mapBranchDetailDataToInterface(data.branch),
      userPermissions: this.mapUserPermissionsDataToInterfaces(data.user_permissions)
    };
  }

  mapUsersDetailDataToInterfaces(data: any[]): UserDetail[] {
    return data.map(item => this.mapUserDetailDataToInterface(item));
  }

  mapPetTypeDataToInterface(data: any): PetType {
    return {
      id: data.id,
      name: data.name,
      description: data.description,
    };
  }

  mapPetCheckInServiceDataToInterface(data: any): Service {
    return {
      id: data.id,
      name: data.name,
      note: data.note,
    };
  }

  mapPetCheckInServicesDataToInterface(data: any[]): Service[] {
    return data.map(item => this.mapPetCheckInServiceDataToInterface(item));
  }

  mapPetCheckInDataToInterface(data: any): PetCheckIn {
    return {
      id: data.id,
      date: data.date,
      services: this.mapPetCheckInServicesDataToInterface(data.services),
      isCheckedOut: data.is_checked_out,
      checkedOutDate: data.checked_out_date,
      branch: data.branch
    };
  }

  mapPetCheckInsDataToInterface(data: any[]): PetCheckIn[] {
    return data.map(item => this.mapPetCheckInDataToInterface(item));
  }

  mapPetCheckInsDetailDataToInterface(data: any): PetCheckInsDetail {
    return {
      id: data.id,
      type: this.mapPetTypeDataToInterface(data.type),
      name: data.name,
      sex: data.sex,
      code: data.code,
      weight: data.weight,
      lastHeatDate: data.last_heat_date,
      age: data.age,
      breed: data.breed,
      isSpayedNeutered: data.is_spayed_neutered,
      isFriendly: data.is_friendly,
      isSensitiveBodyOrPhysicalRestriction: data.is_sensitive_body_or_physical_restriction,
      sensitiveBodyOrPhysicalRestrictionDetails: data.sensitive_body_or_physical_restriction_details,
      isAllowedPhotoVideoUsage: data.is_allowed_photo_video_usage,
      owner: data.owner!=null? this.mapOwnerDetailDataToInterface(data.owner) : data.owner,
      checkIns: this.mapPetCheckInsDataToInterface(data.check_ins)
    }
  }

  mapQRCodeDetailDataToInterface(data: any): QRCodeDetail {
    return {
      id: data.id,
      petId: data.pet_id,
      petName: data.pet_name,
      ownerFirstName: data.owner_first_name,
      ownerLastName: data.owner_last_name,
      isSharePetName: data.is_share_pet_name,
      isSharePetGender: data.is_share_pet_gender,
      isSharePetImage: data.is_share_pet_image,
      isShareOwnerFirstName: data.is_share_owner_first_name,
      isShareOwnerLastName: data.is_share_owner_last_name,
      isShareOwnerPhone: data.is_share_owner_phone,
      isShareOwnerEmail: data.is_share_owner_email,
      isShareOwnerEmergencyContact: data.is_share_owner_emergency_contact,
      message: data.message,
      originalUrl: data.original_url,
      shortUrl: data.short_url,
    }
  }

  mapQRCodesDetailDataToInterfaces(data: any[]): QRCodeDetail [] {
    return data.map(item => this.mapQRCodeDetailDataToInterface(item));
  }

  // mapInvoiceItemDataToInterface(data: any): InvoiceDetail {
  //   return {
  //     id: data.id,
  //     date: data.date,
  //     code: data.code,
  //     checkInId: data.check_in_id,
  //     checkInDate: data.check_in_date,
  //     checkOutId: data.check_out_id,
  //     checkOutDate: data.check_out_date,
  //     petId: data.pet_id,
  //     petName: data.pet_name,
  //     petCode: data.pet_code,
  //     petWeight: data.pet_weight,
  //     ownerId: data.owner_id,
  //     ownerName: data.owner_name,
  //     ownerPhone: data.owner_phone,
  //     ownerEmail: data.owner_email,
  //     ownerAddress: data.owner_address,
  //     ownerTypeId: data.owner_type_id,
  //     ownerTypeName: data.owner_type_name,
  //     serviceCharges: ServiceCharge[];
  //   };
  // }

  // Add more mapping methods as needed
}
